/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useState, useMemo, useEffect, useRef, useCallback,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { memoize } from 'lodash';
import { isEqual } from 'underscore';
import Socket, { ConnectedUser } from '../../connection/socket';
import { VideoStream } from '../types/videos';
import {
	getFullAssetURI,
	toMap,
	hexToRgb,
	numberToPercent,
	percMultiply,
	convertPxToRem,
	getVideoRect,
	calculateBorderSize,
	isVideo,
} from '../../utils/utils';
import V_LAYOUTS, { fixedLayoutTypes } from '../../utils/video-layout';
import { GreenroomState as GS } from '../../stores/types/types';
import { Shot, ShotFeed, FEED_TYPES } from '../../stores/reducers/shots';
import FeedPlaceholder from './feed-placeholder';
import FeedMedia from './feed-media';
import FeedPresenter from './feed-presenter';
import FeedEmail from './feed-email';
import FeedScreenShare from './feed-screenshare';
import VideoButtons from './video-buttons';
import Title from './title';
import CustomGraphicsCanvas, { CanvasLocation } from '../custom-graphics/custom-graphics-canvas';
import OverlayDropArea from '../custom-graphics/overlay-drop-area';
import { videoGridClasses } from '../../types/constants';
import useShots from '../../contexts/shots/provider';
import {
	DRAG_TYPE, DragItem, DropAreaData, DropCallbackData,
} from '../../stores/reducers/user-interface';
import { showAlert } from '../general-components/alert-service';
import { ALERT_TYPES } from '../general-components/alert';
import AudioOnlyFeeds from './audio-only-feeds';
import {
	MediaItem,
	PresentedMediaItem,
	VideoState,
} from '../../stores/reducers/media';
import { setShowbuilderMedia as reduxSetShowbuilderMedia } from '../../stores/actions/media';
import TitleDropArea from '../custom-graphics/title-drop-area';
import { SET_CURRENT_GRAPHICS_ID, setCurrentGraphicsId } from '../../stores/actions/graphics';
import { MediaControlsDataSetState, useGetAllMediaControls } from '../../contexts/media-controls';
import * as keyListeners from '../../utils/keyboard-handler';
import { videoActionCommands } from '../../utils/video-machine';
import { getFileExtension } from '../../utils/media';
import usePrevious from '../../utils/use-previous';

interface OwnProps {
	isForShotEditorModal: boolean;
	isHost: boolean;
	iterateLoadedMediaCount: Function;
	socket: Socket | null;
	shotBeingEdited: Shot;
	useAgora: boolean;
	useTwilio: boolean;
}

export default function MainVideoEditing(props: OwnProps) {
	const {
		isForShotEditorModal,
		iterateLoadedMediaCount,
		shotBeingEdited,
		socket,
	} = props;
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const typeOfDragging = useSelector((s: GS) => s.UserInterfaceReducer.typeOfDragging);
	const globalIsDragging = useSelector((s: GS) => s.UserInterfaceReducer.globalIsDragging);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const canvasStyle = useSelector((s: GS) => s.ShotsReducer.canvasStyle);
	const currentGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const mediaModalOpen = useSelector((s: GS) => s.MediaReducer.mediaModalOpen);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);

	const dispatch = useDispatch();
	const {
		feedDroppedOnCanvas,
		overlayDroppedOnCanvas,
		titleDroppedOnCanvas,
	} = useShots();
	const allMediaControls = useGetAllMediaControls();

	const {
		backgroundColor,
		backgroundType, // image, video, or color. Video not yet implemented
		wallpaperColor, // color in hex
		backgroundImage,
		backgroundScale, // a percentage to fill
		presenterVideoBorderOn, // borders on individual videos
		presenterVideoBorderColor, // hex color
		presenterVideoBorderOpacity, // 0.0 to 1.1
		// center, inside, or outside (i'm not sure this is possible tbh since
		// it would just be a css border prop)
		presenterVideoBorderPosition,
		presenterVideoBorderSize, // width of video border
		presenterVideoRadius,
		layoutSpacingPresets,
	} = canvasStyle;

	const {
		shot_data: {
			forcedLayout,
			shotFeeds,
			shotGraphics,
			videoTitlesOn,
		},
	} = shotBeingEdited;

	const {
		PLAY,
		PAUSE,
	} = videoActionCommands;

	const [videoWidthHeight, setVideoWidthHeight] = useState<any>(null);
	const videoWrapperRef = useRef<any>(null);
	const [showbuilderMedia, setShowbuilderMedia] = useState<PresentedMediaItem[]>([]);
	const [showAudioOnlyFeedsButton, setShowAudioOnlyFeedsButton] = useState(false);

	const standardFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);
	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);

	const resizeObserver = new ResizeObserver((entries) => {
		// 177%, 56.25%
		const wrapWidth = entries[0].contentRect.width;
		const wrapHeight = entries[0].contentRect.height;

		const styleObject = getVideoRect(wrapWidth, wrapHeight);

		setVideoWidthHeight(styleObject);
	});

	useEffect(() => {
		setShowAudioOnlyFeedsButton(true);
	}, []);

	const allGraphics = useMemo((): number[] => [
		...(shotGraphics || []),
		...graphics.filter((g) => !!g.is_global).map((g) => g.id ?? 0),
	], [shotGraphics, graphics]);
	const prevAllGraphics = usePrevious(allGraphics) ?? [];

	useEffect(() => {
		if (!isForShotEditorModal && currentGraphicId && !isEqual(allGraphics, prevAllGraphics)) {
			const inShotGraphics = allGraphics.includes(currentGraphicId);
			if (!inShotGraphics) {
				// when shot changes clear the currently selected graphic
				dispatch(setCurrentGraphicsId(0));
			}
		}
	}, [allGraphics, prevAllGraphics, isForShotEditorModal]);

	useEffect(() => {
		if (videoWrapperRef.current) { resizeObserver.observe(videoWrapperRef.current); }

		return () => {
			if (videoWrapperRef.current) resizeObserver.disconnect();
		};
	}, [videoWrapperRef.current]);

	const backgroundStyle: any = useMemo(() => {
		const newStyle: any = {};
		newStyle.backgroundColor = backgroundColor;
		if (backgroundType === 'image') {
			newStyle.backgroundSize = `${backgroundScale}%`;
			newStyle.backgroundImage = `url('${getFullAssetURI(backgroundImage)}')`;
		}
		newStyle.display = 'grid';
		return newStyle;
	}, [backgroundColor, backgroundImage, backgroundScale, backgroundType]);

	const marginMemo = useMemo(() => {
		const isFixedLayout = forcedLayout
			? fixedLayoutTypes.some((layout) => forcedLayout.includes(layout))
			: false;

		if (shotBeingEdited && isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
			};
		} if (shotBeingEdited) {
			const layoutMap = toMap('layout', layoutSpacingPresets);
			// if no layout exists set no margins
			const layout = layoutMap.get(forcedLayout);
			const backup = {
				spacing: {
					top: 0, bottom: 0, side: 0, inner: 0,
				},
			};
			const margins = layout?.spacing ? layout : backup;
			const {
				inner, top, side, bottom,
			} = margins.spacing;
			return {
				columnGap: numberToPercent(inner),
				rowGap: percMultiply(numberToPercent(inner) || '0%'),
				paddingTop: numberToPercent(top),
				paddingBottom: numberToPercent(bottom),
				paddingRight: numberToPercent(side),
				paddingLeft: numberToPercent(side),
			};
		}

		return {
			columnGap: 0,
			rowGap: 0,
			paddingTop: 0,
			paddingBottom: 0,
			paddingRight: 0,
			paddingLeft: 0,
		};
	}, [forcedLayout, layoutSpacingPresets]);

	const [r, g, b] = useMemo(() => hexToRgb(presenterVideoBorderColor), [presenterVideoBorderColor]);

	const getVidStreamFromId = (id: number) => {
		const feed = connectedPresenterVideos.find((vid: VideoStream) => vid.profile === id);
		return feed;
	};

	const getScreenShareFromEmail = (email: string) => {
		const connUser = connectedUsers.find(
			(connectedUser: ConnectedUser) => (
				connectedUser.email === email && connectedUser.screenShareWebrtcId
			),
		);
		return connectedPresenterVideos.find(
			(vid: VideoStream) => vid.id === connUser?.screenShareWebrtcId,
		);
	};

	const hasPictureInPictureMemo = useMemo(
		() => shotBeingEdited.shot_data.forcedLayout.includes('picture-in-picture'),
		[forcedLayout],
	);

	const numberOfVideosOnCanvas = () => {
		const vidNumber = shotBeingEdited.shot_data.shotFeeds.reduce(
			(previousValue:number, currentValue:ShotFeed) => {
				const singleMedia = media.find((elm) => elm.id === currentValue.media);
				let videoCheck = false;
				if (singleMedia) {
					videoCheck = isVideo(singleMedia.type);
				}
				return previousValue + (videoCheck ? 1 : 0);
			},
			0,
		);
		return vidNumber;
	};

	// we resolve drops in this component because it's the common ancestor
	// of the feed components, so we can pass down the function to call on drop
	const feedDrop = async (item: DragItem, dropAreaData?: DropAreaData) => {
		const mediaBeingReplacedId = shotBeingEdited.shot_data.shotFeeds.find(
			(shot) => shot.position === dropAreaData?.feedPosition,
		)?.media;
		const mediaItemBeingReplaced = media.find((mediaItem) => mediaItem.id === mediaBeingReplacedId);
		const replacingItem = media.find((mediaItem) => mediaItem.id === item.id);
		const beingReplacedIsAVideo = isVideo(mediaItemBeingReplaced?.type || '');
		const replacingItemIsVideo = isVideo(
			item.template && typeof item.id === 'string'
				? getFileExtension(item.id)
				: replacingItem?.type || '',
		);
		const videoIsInCanvas = shotFeeds.some(
			(shot) => shot.media === replacingItem?.id,
		);

		const canDropItem = replacingItemIsVideo
			? (numberOfVideosOnCanvas() < 8)
				|| (numberOfVideosOnCanvas() === 8 && beingReplacedIsAVideo) || videoIsInCanvas
			: true;
		if (typeof dropAreaData?.feedPosition === 'number' && canDropItem) {
			feedDroppedOnCanvas(item, socket, isForShotEditorModal, dropAreaData.feedPosition);
		} else {
			showAlert({
				type: ALERT_TYPES.WARNING,
				message: 'Video Capacity Reached',
				description: 'Only 8 videos are allowed on the canvas at a given time',
				duration: 5000,
			});
		}
	};

	const overlayDrop = useCallback((item: DragItem, dropData: DropCallbackData) => {
		overlayDroppedOnCanvas(item, socket, isForShotEditorModal, dropData);
	}, [overlayDroppedOnCanvas, socket, isForShotEditorModal]);

	const titleDrop = useCallback((item: DragItem, dropData: DropCallbackData) => {
		titleDroppedOnCanvas(item, socket, isForShotEditorModal, dropData);
	}, [titleDroppedOnCanvas, socket, isForShotEditorModal]);

	useEffect(() => {
		const shotMediaIds = shotBeingEdited.shot_data.shotFeeds
			.filter((feed: ShotFeed) => feed.feedType === FEED_TYPES.MEDIA)
			.map((feed: ShotFeed) => feed.media);

		const updatedShowbuilderMedia = showbuilderMedia.map((item) => {
			// This resets the video state in the showbuilderMedia array
			// if the media item is not in the current shot
			if (!shotMediaIds.includes(item.mediaId)) {
				return {
					...item,
					videoState: {
						paused: true,
						timeElapsed: 0,
						volume: 1,
					},
				};
			}

			// This carries over the elapsed value for a new shot
			// if the same media item is playing in a different feed position
			if (item.videoState?.timeStarted && !item.videoState?.paused) {
				const timeStart = item.videoState?.timeStarted.valueOf();
				const newTimeElapsed = ((new Date().valueOf() - timeStart) / 1000);
				return {
					...item,
					videoState: {
						...item.videoState,
						timeElapsed: newTimeElapsed + (item.videoState.timeElapsed ?? 0),
						timeStarted: new Date(),
					},
				};
			}
			return item;
		});

		if (!isEqual(showbuilderMedia, updatedShowbuilderMedia)) {
			setShowbuilderMedia(updatedShowbuilderMedia);
		}
	}, [shotBeingEdited]);

	// TODO: This is not an ideal way of handling state (sending local to redux)
	// A better solution is needed, but this will make it work for now
	useEffect(() => {
		dispatch(reduxSetShowbuilderMedia(showbuilderMedia));
	}, [showbuilderMedia]);

	const updateShowbuilderMedia = (itemUpdate: PresentedMediaItem, videoUpdate?: VideoState) => {
		setShowbuilderMedia((updatedShowbuilderMedia) => updatedShowbuilderMedia.map(
			(showboardMediaItem: PresentedMediaItem) => {
				if (showboardMediaItem.mediaId === itemUpdate.mediaId) {
					if (videoUpdate) {
						return {
							...showboardMediaItem,
							videoState: {
								...showboardMediaItem.videoState as VideoState,
								...videoUpdate,
							},
						};
					}
					return itemUpdate;
				}
				return showboardMediaItem;
			},
		));
	};

	const getShowbuilderMediaItem = memoize((mediaId: number) => (
		showbuilderMedia.find((med: PresentedMediaItem) => med.mediaId === mediaId)
	));

	// If video, rerenders the media feed component, to reset the video machine references.
	// If image, to avoid long blank render between slides by not changing key.
	const getKeyForMediaFeed = memoize((id: number): string => {
		const presMediaItem = showbuilderMedia.find((med: PresentedMediaItem) => med.mediaId === id);
		const medItem = media.find((m) => m.id === id);
		if (presMediaItem && medItem) {
			const page = medItem?.media_data.image_array[presMediaItem.currentPage - 1];
			if (page && isVideo(page)) {
				return `${presMediaItem.currentPage}-${id}`;
			}
		}
		return `1-${id}`;
	});

	// const allVideosArePaused = useMemo((): boolean => (
	// 	Object.values(allMediaControls).every(({ state: paused }) => paused)
	// ), [allMediaControls.state.paused]);

	const setVideosPlaying = (
		mediaControls: MediaControlsDataSetState,
		isModalOpen: boolean = false,
	): void => {
		const options = {
			doFireSync: true,
			from: 'space bar key listener',
		};

		if (isModalOpen || document.getElementById('modal-large-overlay')) {
			return;
		}

		const allVideosArePaused = Object.values(mediaControls).every((mediaControl) => mediaControl?.state?.paused);

		Object.values(mediaControls).forEach(({ send }) => {
			if (allVideosArePaused) {
				send?.(PLAY, options);
			} else {
				send?.(PAUSE, options);
			}
		});
	};

	useEffect(() => {
		const isModalOpen = mediaModalOpen || modalOpen;

		keyListeners.addListener(keyListeners.KEYS.KEY_SPACE, () => {
			setVideosPlaying(allMediaControls, isModalOpen);
		});
	}, [allMediaControls, mediaModalOpen, modalOpen]);

	function videosInCanvas(
		selectedShotFeeds: ShotFeed[],
		startAt: number = 0,
		audioOnly: boolean = false,
	) {
		return (
			<>
				{selectedShotFeeds && selectedShotFeeds.map((shotFeedRef: ShotFeed, mapIndex: number) => {
					const shotFeed = { ...shotFeedRef };
					const index = startAt + mapIndex;

					// if a user was put into a shot before creating an account and joins the room while
					// the shot is on the canvas, change the feed from email to presenter to put them in
					// the canvas without editing the shot and forcing it to rerender
					if (shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email) {
						const connUser = connectedUsers.find(
							(connectedUser: ConnectedUser) => connectedUser.email === shotFeed.email,
						);
						if (connUser && connUser.gr_user) {
							shotFeed.feedType = FEED_TYPES.PRESENTER;
							shotFeed.presenter = connUser.gr_user;
							shotFeed.email = null;
						}
					}

					if (
						(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO)
						&& shotFeed.media
						&& !getShowbuilderMediaItem(shotFeed.media)
					) {
						const med = media.find((m: MediaItem) => m.id === shotFeed.media);
						if (med) {
							const newItem = {
								mediaId: med?.id ?? 1,
								currentPage: 1,
								pages: med?.media_data.image_array ?? [],
								name: med?.custom_name ?? med?.original_name ?? '',
								videoState: isForShotEditorModal ? {
									muted: true, paused: true, volume: 1, timeElapsed: 0,
								} : {
									muted: false, paused: true, volume: 1, timeElapsed: 0,
								},
							};
							setShowbuilderMedia([...showbuilderMedia, newItem]);
						}
					}

					// dry this up a bit
					const sizing = convertPxToRem(
						calculateBorderSize('.main-video', presenterVideoBorderSize, 0),
					);
					const color = `rgba(${r}, ${g}, ${b}, ${presenterVideoBorderOpacity})`;
					const insideStyle: any = {
						borderRadius: presenterVideoRadius,
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						boxShadow: presenterVideoBorderOn
							? `inset 0 0 0 ${sizing}rem ${color}`
							: 'none',
					};

					return (
						<React.Fragment key={mapIndex}>
							<div key={mapIndex} className={classNames('video-container-presenter', videoGridClasses[index])}
								style={{
									borderRadius: presenterVideoRadius,
									boxShadow: presenterVideoBorderOn && presenterVideoBorderPosition === 'outside'
										? `0 0 0 ${sizing}rem ${color}`
										: 'none',
									display: (forcedLayout === V_LAYOUTS.TWO.FULL_TWO && index !== 1) ? 'none' : 'block',
								}}>
								{shotFeed.feedType === FEED_TYPES.EMPTY && (
									<FeedPlaceholder
										feedPosition={index}
										dropCallback={feedDrop}
									/>
								)}
								{(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO) && shotFeed.media
									&& <FeedMedia
										key={getKeyForMediaFeed(shotFeed.media)}
										feedPosition={index}
										mediaId={shotFeed.media}
										socket={socket}
										isForMainVideoEditingComponent={true}
										iterateLoadedMediaCount={iterateLoadedMediaCount}
										isEditingFeed
										isForShotEditorModal={isForShotEditorModal}
										dropCallback={feedDrop}
										audioOnly={audioOnly}
										showbuilderMediaItem={getShowbuilderMediaItem(shotFeed.media)}
										updateShowbuilderMedia={updateShowbuilderMedia}
									/>
								}
								{shotFeed.feedType === FEED_TYPES.PRESENTER && shotFeed.presenter && (
									<FeedPresenter
										feedPosition={index}
										video={getVidStreamFromId(shotFeed.presenter)}
										grUser={shotFeed.presenter}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email && (
									<FeedEmail
										feedPosition={index}
										email={shotFeed.email}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.SCREENSHARE && shotFeed.screenshare && (
									<FeedScreenShare
										feedPosition={index}
										video={getScreenShareFromEmail(shotFeed.screenshare)}
										email={shotFeed.screenshare}
										dropCallback={feedDrop}
									/>
								)}
								{/* this was added because when there was no feed like screenshare
								//stopped the inside border did not show */}
								{presenterVideoBorderPosition === 'inside' && (
									<div style={insideStyle} />
								)}
								{shotFeed.presenter && (
									<Title
										key={currentShotId}
										idOfPresenter={shotFeed.presenter}
										shotId={currentShotId}
										videoTitlesOn={videoTitlesOn}
									/>
								)}
							</div>
						</React.Fragment>
					);
				})}
			</>
		);
	}

	return (
		<>
			<div id="video-wrapper" ref={videoWrapperRef} className={classNames(
				'include-audio-only-feeds-edit',
				{ embed },
			)}>
				{shotBeingEdited && (
					<div style={{ ...backgroundStyle, ...videoWidthHeight }} className="main-video-container">
						{videoWidthHeight
							&& <CustomGraphicsCanvas
								renderedGraphics={shotGraphics}
								canEdit={true}
								videoWidthHeight={videoWidthHeight}
								shot={shotBeingEdited}
								isForShotEditorModal={isForShotEditorModal}
								socket={socket}
								location={isForShotEditorModal ? CanvasLocation.ShotEditor : ''}
							/>
						}
						{hasPictureInPictureMemo ? (
							<>
								{!!audioOnlyFeeds.length && (
									<div
										style={marginMemo}
										className={classNames(
											'main-video hidden',
											shotBeingEdited.shot_data.forcedLayout,
											{ backgroundColor: wallpaperColor },
										)}
									>
										{videosInCanvas(audioOnlyFeeds, 0, true)}
									</div>
								)}
								<div
									style={marginMemo}
									className={classNames(
										'main-video layer1',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds.slice(1), 1)}
								</div>
								<div
									className={classNames(
										'main-video layer2',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds.slice(0, 1))}
								</div>
							</>
						) : (
							<>
								{!!audioOnlyFeeds.length && (
									<div
										style={marginMemo}
										className={classNames(
											'main-video hidden',
											shotBeingEdited.shot_data.forcedLayout,
											{ backgroundColor: wallpaperColor },
										)}
									>
										{videosInCanvas(audioOnlyFeeds, 0, true)}
									</div>
								)}
								<div
									style={marginMemo}
									className={classNames(
										'main-video',
										shotBeingEdited.shot_data.forcedLayout,
										{ backgroundColor: wallpaperColor },
									)}
								>
									{videosInCanvas(standardFeeds)}
								</div>
							</>
						)}
						<div
							id="controls-container-editing"
							data-testid="controls-container-editing"
							className={classNames('main-video', shotBeingEdited.shot_data.forcedLayout)}
							style={{ ...marginMemo, pointerEvents: globalIsDragging ? 'none' : 'inherit' }}
						/>
						{(typeOfDragging === DRAG_TYPE.OVERLAY)
							&& <OverlayDropArea dropCallback={overlayDrop} />
						}
						{(typeOfDragging === DRAG_TYPE.TITLE)
							&& <TitleDropArea dropCallback={titleDrop} />
						}
						{!embed && !host && (
							<div className="left-panel-below-video-setup">
								<div className="lower-setup-button-container">
									{showAudioOnlyFeedsButton && (
										<AudioOnlyFeeds
											isForShotEditorModal={false}
											socket={socket}
										/>
									)}
									{!isForShotEditorModal && (
										<div id="portal-unmute-snackbar-2" />
									)}
								</div>
							</div>
						)}
						<div className="left-panel-below-video-setup">
							<VideoButtons
								socket={socket}
								shotBeingEdited={shotBeingEdited}
								isForShotEditorModal={isForShotEditorModal}
								// treated as 0th indexed feed
								feedPosition={0}
								dropCallback={feedDrop}
								/>
						</div>
					</div>
				)}
			</div>
			<div id="feed-media-context-menu-portal" />
			<div id="layout-modal-portal" />
			<div id="video-hover-options-portal" />
		</>
	);
}
